import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";

function formatTimeLeft(time) {
  // The largest round integer less than or equal to the result of time divided being by 60.
  const minutes = Math.floor(time / 60);

  // Seconds are the remainder of the time divided by 60 (modulus operator)
  let seconds = time % 60;

  // If the value of seconds is less than 10, then display seconds with a leading zero
  if (seconds < 10) {
    seconds = `0${seconds}`;
  }

  // The output in MM:SS format
  return `${minutes} : ${seconds}`;
}

export default function Countdown({ timeLimit, setMsgVisible }) {
  const [circleDash, setCircleDash] = useState("283");
  const [timePassed, setTimePassed] = useState(0);
  const [timeLeft, setTimeLeft] = useState(timeLimit);

  // Start with an initial value of 20 seconds
  // const TIME_LIMIT = 20;
  const COLOR_CODES = {
    info: {
      color: "#E50019",
    },
  };
  const FULL_DASH_ARRAY = 283;
  let remainingPathColor = COLOR_CODES.info.color;

  // Divides time left by the defined time limit.
  const calculateTimeFraction = useCallback(() => {
    const rawTimeFraction = timeLeft / timeLimit;
    return rawTimeFraction - (1 / timeLimit) * (1 - rawTimeFraction);
  }, [timeLeft, timeLimit]);

  // Update the dasharray value as time passes, starting with 283
  const setCircleDasharray = useCallback(() => {
    const circleDasharray = `${(
      calculateTimeFraction() * FULL_DASH_ARRAY
    ).toFixed(0)} 283`;
    setCircleDash(circleDasharray);
  }, [calculateTimeFraction]);

  useEffect(() => {
    const timeInterval =
      timeLeft > 0 &&
      setInterval(() => {
        /* The amount of time passed increments by one */
        // Initially, no time has passed, but this will count up
        // and subtract from the TIME_LIMIT
        setTimePassed((prev) => prev + 1);
        setTimeLeft(timeLimit - timePassed);
        setCircleDasharray();
      }, 1000);

    if (timeLeft === 0) setMsgVisible(true);

    return () => clearInterval(timeInterval);
  }, [timeLimit, timeLeft, timePassed, setCircleDasharray, setMsgVisible]);

  return (
    <Timer remainingPathColor={remainingPathColor}>
      <div className="base-timer">
        <svg
          className="base-timer__svg"
          viewBox="0 0 100 100"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g className="base-timer__circle">
            <circle class="base-timer__path-elapsed" cx="50" cy="50" r="45" />
            <path
              id="base-timer-path-remaining"
              strokeDasharray={circleDash}
              className={`base-timer__path-remaining ${remainingPathColor}`}
              d="M 50, 50
                                m -45, 0
                                a 45,45 0 1,0 90,0
                                a 45,45 0 1,0 -90,0
                                "
            ></path>
          </g>
        </svg>
        <span id="base-timer-label" className="base-timer__label">
          {/* Remaining time label */}
          {formatTimeLeft(timeLeft)}
        </span>
      </div>
    </Timer>
  );
}

const Timer = styled.div`
  display: grid;
  place-items: center;
  margin-bottom: 20px;

  /* Sets the containers height and width */
  .base-timer {
    position: relative;
    height: 80px;
    width: 80px;
  }

  /* Removes SVG styling that would hide the time label */
  .base-timer__circle {
    fill: none;
    stroke: none;
  }

  /* The SVG path that displays the timer's progress */
  .base-timer__path-elapsed {
    stroke-width: 7px;
    stroke: grey;
  }

  .base-timer__label {
    position: absolute;

    /* Size should match the parent container */
    width: 80px;
    height: 80px;

    /* Keep the label aligned to the top */
    top: 0;

    /* Create a flexible box that centers content vertically and horizontally */
    display: flex;
    align-items: center;
    justify-content: center;

    /* Sort of an arbitrary number; adjust to your liking */
    font-size: 18px;
  }
  .base-timer__path-remaining {
    /* Just as thick as the original ring */
    stroke-width: 7px;

    /* Rounds the line endings to create a seamless circle */
    stroke-linecap: round;

    /* Makes sure the animation starts at the top of the circle */
    transform: rotate(90deg);
    transform-origin: center;

    /* One second aligns with the speed of the countdown timer */
    transition: 1s linear all;

    /* Allows the ring to change color when the color value updates */
    stroke: ${({ remainingPathColor }) => remainingPathColor};
  }

  .base-timer__svg {
    /* Flips the svg and makes the animation to move left-to-right */
    transform: scaleX(-1);
  }
`;
